/*
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */
import React, {useEffect, useState} from 'react';
import {Form, Formik, FormikHelpers} from 'formik';
import {StyledProvisioningPageHeading} from '../StyledProvisioningPageHeading';
import {StyledProvisioningPageSubheading} from '../StyledProvisioningPageSubheading';
import {Stack} from '@mui/material';
import {BeamInput} from '../../common/formik/BeamInput';
import ErrorMessage from '../../common/layout/ErrorMessage';
import {BeamCancelButton} from '../../common/formik/BeamCancelButton';
import {BeamSubmitButton} from '../../common/formik/BeamSubmitButton';
import { useParams } from 'react-router-dom';
import {ajaxRaw} from '../../../utils/StoreUtils';
import * as yup from 'yup';
import Contract from '../../../shared/entities/contract';
import {useDispatch, useSelector} from 'react-redux';
import {selectAvailableServiceTiers} from '../../../store';
import {getAvailableServiceTiers} from '../../../store/serviceTierRequests/actions';
import {BeamSelect} from '../../common/formik/BeamSelect';
import {BeamTerminalSelect} from '../../common/formik/BeamTerminalSelect';
import {BeamDatePicker} from '../../common/formik/BeamDatePicker';
import ModalLoadingSpinner from "../../common/layout/ModalLoadingSpinner";
import { useAjaxFormSubmit } from '../../common/formik/useAjaxFormSubmit';

export const ContractsForm = () => {
  const dispatch = useDispatch();
  let { uuid } = useParams();

  const serviceTiers = useSelector(selectAvailableServiceTiers);

  const [submissionError, setError] = useState<string | undefined>(undefined);
  const [initialFormState, setInitialFormState] = useState<Partial<Contract>>({
    id: '',
    clin: '',
    contractName: '',
    startDate: '',
    endDate: '',
    simops: 0,
    terminals: [],
  });

  // Run actions on initial load
  useEffect(() => {
    // Request options
    dispatch(getAvailableServiceTiers());
  }, [dispatch]);

  const isEditMode = uuid !== undefined;
  const action = isEditMode ? 'Edit' : 'Add';

  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    // Request existing data if we're editing an entity
    if (isEditMode) {
      setIsLoading(true);
      ajaxRaw.get(`/provisioning/contracts/${uuid}`).subscribe({
        next: value => {
          setInitialFormState(value.response as Partial<Contract>);
        },
        error: err => setError(err.message),
        complete: () => setIsLoading(false),
      });
    }
  }, [isEditMode, uuid]);

  const ajaxFormSubmit = useAjaxFormSubmit();

  return (
    <>
      <StyledProvisioningPageHeading>
        {action} {!isEditMode && 'new '}contract
      </StyledProvisioningPageHeading>
      <StyledProvisioningPageSubheading>
        To {action.toLowerCase()} a {!isEditMode && 'new '}contract, please complete the form below.
      </StyledProvisioningPageSubheading>

      {isLoading ? <ModalLoadingSpinner /> : null}

      <Formik
        enableReinitialize={true}
        initialValues={initialFormState}
        onSubmit={(values, { setSubmitting }: FormikHelpers<Partial<Contract>>) => {
          setError(undefined);
          setSubmitting(true);

          let url = `/provisioning/contracts`;
          let operation = ajaxRaw.postJson;
          if (isEditMode) {
            url = `/provisioning/contracts/${values.id}`;
            operation = ajaxRaw.put;
          }

          ajaxFormSubmit(operation, url, values, setError, setSubmitting);
        }}
        validateOnChange={true}
        validationSchema={yup.object().shape({
          contractName: yup
            .string()
            .required('Please enter a name for the contract.')
            .max(255, 'Contract name must be less than 255 characters.'),
          startDate: yup
            .date()
            .max(yup.ref('endDate'), 'Start date cannot be after end date.')
            .required('Please enter a start date.'),
          endDate: yup
            .date()
            .min(yup.ref('startDate'), 'End date cannot be before start date.')
            .required('Please enter an end date.'),
          clin: yup.string().required('Please select a CLIN.'),
          simops: yup.number().required('Please enter a number for SIMOPS.').typeError('Please enter a number.'),
          terminals: yup.array(),
        })}
      >
        <Form style={{ width: '600px', paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '18px' }}>
          
          {submissionError ? <ErrorMessage>{submissionError}</ErrorMessage> : null}

          <BeamInput name={'contractName'} label={'Contract Name'} required={true}></BeamInput>
          <BeamDatePicker name={'startDate'} label={'Start Date'} required={true} />
          <BeamDatePicker name={'endDate'} label={'End Date'} required={true} />
          <BeamSelect
            name={'clin'}
            label={'CLIN'}
            options={serviceTiers ?? []}
            placeholder={'Select CLIN'}
            required={true}
            disabled={isEditMode}
            helpText={'CLIN cannot be changed after contract is created.'}
          />
          <BeamInput name={'simops'} label={'SIMOPS'} required={true}></BeamInput>
          <BeamTerminalSelect name={'terminals'} label={'Terminals'} required={true} />
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <BeamCancelButton />
            <BeamSubmitButton />
          </Stack>
        </Form>
      </Formik>
    </>
  );
};

export default ContractsForm;
